import { get, isUndefined, forEach, omitBy, size, uniq, includes, isEmpty, entries, forOwn } from 'lodash';
import kebabCase from 'lodash/kebabCase';
import React, { Component, CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import produce from 'immer';
import { clientLog } from 'common/utils/helpers';
import { isLayerType } from 'common/utils/is-layer-type';
import { orderMediaQueryConfig } from 'common/utils/story-media-query';
import {
	COMPONENT_STATES_ORDERED,
	COMPONENT_STATES,
	CUSTOM_CSS,
	CARD_ROOT_ELEMENT_INDEX,
	COMPONENT_TYPE,
	GLOBAL_BLOCK_DISPLAY,
	CSS_PROPS,
	COMPONENT_STATES_LIST,
} from 'common/constants';
import { attrSelector } from 'client/utils';
import { getElementSymbol } from 'client/components/common/BuildingBlocks/utils/traverse-tree';
import { BBModel, StoryMediaQuery, StorySymbols } from 'types/story';
import { getCustomCssProp } from 'utils/get-custom-css-prop';
import { bgImage, getOptimizedSrc } from 'utils/assets';

type Props = {
	elements: BBModel[];
	mediaQuery: StoryMediaQuery;
	isCardTree: boolean;
	isEditor: boolean;
	symbols?: StorySymbols;
};

type MapCssObject = {
	cssObject: CSSProperties;
	selector: string;
	addTab?: boolean;
	element: BBModel;
	mq: ReturnType<typeof orderMediaQueryConfig>;
};

/*------------------------------------------------------------------------
Styles

Order:
1 first, the usual selectors (id) ordered by media queries
2 then state overrides (state data attribute selector)

Selector types:
1. id
2. id + state data attribute selector

How state selector works
- When working in client side, we manage attributes in Component to apply specific state styles to DOM element
- When working in editor:
    -> initialize state in Admin (cardEditorReducer.state)
     -> transmit state to client
      -> assign state selector to "uiConfig.nodeProps.<data-state>"
        -> assigning this attr in DOM
------------------------------------------------------------------------*/

class StylesRenderer extends Component<Props> {
	log = clientLog.extend('Styles');

	/**
	 * @info 💡💡💡 If you're adding a new compound selector,
	 * you've to add his implementation in "traverseTree" and his getComputedValue too
	 *
	 * @description get css selector accordingly to state and environment
	 * @param state {String}
	 * @param isEditor {Boolean}
	 * @return {*}
	 */
	stateSelector = ({ state }) => {
		return state === COMPONENT_STATES.DEFAULT ? '' : attrSelector(state);
	};

	/**
	 * mapChildren -> mapStyles (mapStates, mapMediaQueries) -> mapCssObject
	 * @return {string}
	 */
	transformStylesToString() {
		const { elements, mediaQuery } = this.props;
		let styles = '';
		const t0 = performance.now();

		// eg. default, min1024, min1600, max800 max700
		const orderedMediaQueryArray = orderMediaQueryConfig(mediaQuery, {
			defaultFirst: true,
			minOrder: 'asc',
			maxOrder: 'desc',
		});

		const mapChildren = (children, parent = {}) => forEach(children, child => mapStyles(child, parent));

		/**
		 * Inject style stored in other component (source) to the desired one (destination)
		 * @param state
		 * @param mq
		 * @param destination
		 * @param element
		 */
		const injectStyles = ({ state, mq, destination, element }) => {
			const cardOtherProps = elements[CARD_ROOT_ELEMENT_INDEX[COMPONENT_TYPE.CARD]].uiConfig.componentProps.other;

			const source = {
				[COMPONENT_TYPE.CONTENT]: {
					height: get(cardOtherProps, `${state}.${mq}.contentHeight`),
					marginTop: get(cardOtherProps, `${state}.${mq}.contentMarginTop`),
					marginBottom: get(cardOtherProps, `${state}.${mq}.contentMarginBottom`),
					marginLeft: get(cardOtherProps, `${state}.${mq}.contentMarginLeft`),
					marginRight: get(cardOtherProps, `${state}.${mq}.contentMarginRight`),
				},
			};

			return produce(destination, draft => {
				if (source[element.type]) {
					Object.assign(draft, omitBy(source[element.type], isUndefined));
				}
			});
		};

		const mapStyles = (elementOrigin, parent) => {
			const element = getElementSymbol(elementOrigin, this.props.symbols || {});
			const elementStyles = get(element, 'uiConfig.componentProps.styles');
			const { isCardTree } = this.props;
			// skip story elements in card elements render
			if (isLayerType(element).global && isCardTree) {
				return;
			}

			if (!elementStyles) {
				// go recursively down children
				mapChildren(element.children);
				return;
			}

			const id = get(element, 'uiConfig.nodeProps.id');

			const nextParent = { ...parent };

			const owsStates = COMPONENT_STATES_ORDERED.filter(s => includes(COMPONENT_STATES_LIST[element.type], s));
			const states = size(parent.states) ? parent.states : owsStates;
			const uniqStates = uniq([COMPONENT_STATES.DEFAULT, ...states]);

			forEach(uniqStates, state => {
				forEach(orderedMediaQueryArray, mq => {
					const { config, key: platform } = mq;
					const mediaProperty = config.minWidth ? 'min-width' : 'max-width';
					const mediaValue = Math.max(config.minWidth ?? 0, config.maxWidth ?? 0);
					const media = `@media (${mediaProperty}: ${mediaValue}px)`;

					// pick element styles for current media query
					let cssObject = get(elementStyles, [state, platform]) || {};
					// @ts-expect-error ts-migrate FIXME
					const stateSelector = this.stateSelector({ state, isEditor: this.props.isEditor });

					let selector = `#${id}${stateSelector}`;
					if (parent.id && parent.id !== id && stateSelector) {
						selector = `#${parent.id}${stateSelector} #${id}`;
					}

					if (stateSelector && !parent.id) {
						nextParent.id = id;
						nextParent.states = states;
					}

					if (this.props.isCardTree) {
						cssObject = injectStyles({ state, mq: platform, destination: cssObject, element });
					}

					// skip to next
					if (isEmpty(cssObject)) {
						return;
					}

					const payload = { cssObject, selector, element, mq: orderedMediaQueryArray };
					if (mediaValue) {
						styles += `${media} {\n`;
						mapCssObject({ ...payload, addTab: true });
						styles += '}\n';
					} else {
						mapCssObject(payload);
					}
				});
			});

			// go recursively down children
			mapChildren(element.children, nextParent);
		};

		const mapCssObject = ({ cssObject, selector, addTab, element, mq }: MapCssObject) => {
			const tab = addTab ? '\t' : '';

			const cssArray = entries(cssObject).sort(([a], [b]) => {
				// Why borderRadius? shorthand property should have less priority against particular property
				if (a === CUSTOM_CSS || b === CSS_PROPS.borders.borderRadius) return 1;
				if (b === CUSTOM_CSS) return -1;
				return a.localeCompare(b);
			});

			// unsupported css rules
			const cssArrayCustom = cssArray.filter(([prop]) => getCustomCssProp(element.type, prop));
			forEach(cssArrayCustom, css => {
				const [property, cssValue] = css;
				const { selectorPostfix, cssProperty, selectorPrefix } = getCustomCssProp(element.type, property, {
					uiConfig: element.uiConfig,
				})!;

				if (property === GLOBAL_BLOCK_DISPLAY) {
					// this property is assigned per card
					forOwn(cssValue, (value, key) => {
						const prefix = selectorPrefix?.({ cardId: key });

						styles += `${tab}${prefix} ${selector} {\n`;
						styles += `${tab}\t${kebabCase(cssProperty)}: ${value};\n`;
						styles += `${tab}}\n`;
					});
				} else {
					const postfix = selectorPostfix?.() || '';
					const prefix = selectorPrefix?.(cssValue as any) || '';

					styles += `${tab}${prefix} ${selector} ${postfix} {\n`;
					styles += `${tab}\t${kebabCase(cssProperty)}: ${cssValue};\n`;
					styles += `${tab}}\n`;
				}
			});

			// supported css rules
			styles += `${tab}${selector} {\n`;
			forEach(cssArray, css => {
				let [cssProperty, cssValue] = css;

				if (element.type !== 'CARD') {
					// v1.9.0 background image is replaced with an <img>, so have to use img properties
					if (cssProperty === 'backgroundSize') cssProperty = 'objectFit';
					if (
						cssProperty === 'backgroundPosition' || // treated manually by target building block
						cssProperty === 'backgroundImage' ||
						cssProperty === 'backgroundRepeat' // not supported
					)
						cssValue = '';
				}

				const hasValue = !isUndefined(cssValue) && cssValue !== '';
				if (hasValue && !getCustomCssProp(element.type, cssProperty)) {
					styles += `${tab}\t${CSSRule(cssProperty, cssValue, { mq: mediaQuery.config })}\n`;
				}
			});
			styles += `${tab}}\n`;
		};

		mapChildren(elements);

		const t1 = performance.now();
		this.log(`rendering took ${(t1 - t0).toFixed(4)} milliseconds. %O `, elements);

		return styles;
	}

	render() {
		return createPortal(
			// @ts-expect-error ts-migrate FIXME
			<style storycards="storycards-css-json">{this.transformStylesToString()}</style>,
			document.head
		);
	}
}

type TCSSValue = string | number;
type TCSSProperty = string;
type TCSSParams = { mq: StoryMediaQuery['config'] };

const CSSValueMap = {
	backgroundImage: (v: TCSSValue, p: TCSSParams) =>
		bgImage(getOptimizedSrc({ src: `${v}`, mq: p.mq })).backgroundImage,
	gridAutoRows: (v: TCSSValue) => `minmax(auto, ${v})`,
	[CUSTOM_CSS]: (v: TCSSValue) => `-->; ${v}`,
	default: (v: TCSSValue) => v,
};

function CSSValue(property: TCSSProperty, value: TCSSValue, params: TCSSParams): TCSSValue {
	return CSSValueMap[property] ? CSSValueMap[property](value, params) : CSSValueMap.default(value);
}

function CSSRule(property: TCSSProperty, value: TCSSValue, params: TCSSParams) {
	return `${kebabCase(property)}: ${CSSValue(property, value, params)};`;
}

export default StylesRenderer;
